import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import atmosphereService, { Atmosphere } from '../../services/atmosphereService';

interface AtmosphereState extends LoadingState {
  atmospheres: Atmosphere[];
  selectedAtmosphere: Atmosphere | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: AtmosphereState = {
  atmospheres: [],
  selectedAtmosphere: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchAtmospheres = createAsyncThunk(
  'atmospheres/fetchAtmospheres',
  async (params: { page?: number; limit?: number; search?: string; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 5, search = '', append = false } = params;
      const response = await atmosphereService.getActiveAtmospheres({ page, limit });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch atmospheres');
    }
  }
);

export const createAtmosphere = createAsyncThunk(
  'atmospheres/createAtmosphere',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await atmosphereService.createAtmosphere(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create atmosphere');
    }
  }
);

const atmosphereSlice = createSlice({
  name: 'atmospheres',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAtmosphere: (state, action: PayloadAction<Atmosphere | null>) => {
      state.selectedAtmosphere = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.atmospheres = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Atmospheres
      .addCase(fetchAtmospheres.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAtmospheres.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.atmospheres.push(...data.data as any);
        } else {
          state.atmospheres = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchAtmospheres.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Atmosphere
      .addCase(createAtmosphere.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAtmosphere.fulfilled, (state, action) => {
        state.isLoading = false;
        const newAtmosphere = action.payload;
        
        // Create a properly typed object for WritableDraft
        const atmosphereToAdd: Atmosphere = {
          _id: newAtmosphere._id,
          name: newAtmosphere.name,
          slug: newAtmosphere.slug,
          status: newAtmosphere.status,
          isDeleted: newAtmosphere.isDeleted,
          createdBy: newAtmosphere.createdBy,
          createdAt: newAtmosphere.createdAt,
          updatedAt: newAtmosphere.updatedAt
        };
        
        state.atmospheres.unshift(atmosphereToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createAtmosphere.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedAtmosphere,
  setSearchQuery,
  resetPagination,
} = atmosphereSlice.actions;

export default atmosphereSlice.reducer;
